<template>
    <v-layout row>
        <v-flex xs12 sm12>

            <!-- ERROR HANDLER -->
            <v-alert v-if="error" :value="true" color="error" icon="error" outline class="rounded-4">
                Error encountered. Please contact support. Error details: {{errorMessage}}
            </v-alert>  

            <!-- ATTENDANCE REMINDERS -->
            <v-card class="elevation-1 mt-5">
                <v-toolbar @click="attendanceReminderVisible=!attendanceReminderVisible" class="clickable" flat height="45px">
                    <span class="font-weight-bold primary--text">ATTENDANCE REMINDERS</span>
                    <v-spacer></v-spacer>

                    <v-btn icon>
                        <v-icon>{{ attendanceReminderVisible ? 'expand_less' : 'expand_more' }}</v-icon>
                    </v-btn>

                </v-toolbar>

                <!-- Reminder Settings -->
                <v-card-text v-if="attendanceReminderVisible" class="ml-20 mr-20">
                    <div class="body-2 text--secondary text--darken-2 mb-10 mr-30">
                            Set up company‑wide attendance reminders to help employees stay on track with their work shifts.
                            Reminders are only sent when a workshift plan is in place—without a plan, no automated emails will be delivered.
                            These settings apply to all employees across the company.
                    </div>

                    <!-- Before Shift Start -->
                    <v-layout row wrap v-if="!loading">
                        <v-flex xs12 sm6 class="form-row">            
                            <v-switch
                                v-model="beforeStart"
                                label="Before Shift Start"
                                color="primary"
                                class="custom-switch"
                                hide-details
                            ></v-switch>
                            <div class="caption grey--text text--darken-2 mt-0 mr-30">
                                Remind employees before their scheduled shift begins if no attendance clock‑in is found
                            </div>
                        </v-flex>
                        <v-flex xs12 sm2 class="form-row">
                            <v-select
                                v-model="beforeStartMinutes"
                                label="Minutes Before Start"
                                :items="allowed_minutes"
                                :disabled="disabled||!beforeStart"
                                class="minutes-select"
                            ></v-select>
                        </v-flex>
                    </v-layout>
                    
                    <!-- After Shift Start -->
                    <v-layout row wrap v-if="!loading">
                        <v-flex xs12 sm6 class="form-row">            
                            <v-switch
                                v-model="afterStart"
                                label="After Shift Start"
                                color="primary"
                                class="custom-switch"
                                hide-details
                            ></v-switch>
                            <div class="caption grey--text text--darken-2 mt-0 mr-30">
                                Remind employees after their shift begins if no attendance clock‑in is found
                            </div>
                        </v-flex>
                        <v-flex xs12 sm2 class="form-row">
                            <v-select
                                v-model="afterStartMinutes"
                                label="Minutes After Start"
                                :items="allowed_minutes"
                                :disabled="disabled || !afterStart"
                                class="minutes-select"
                            ></v-select>
                        </v-flex>
                    </v-layout>

                    <!-- Before Shift End -->
                    <v-layout row wrap v-if="!loading">
                        <v-flex xs12 sm6 class="form-row">            
                            <v-switch
                                v-model="beforeEnd"
                                label="Before Shift End"
                                color="primary"
                                class="custom-switch"
                                hide-details
                            ></v-switch>
                            <div class="caption grey--text text--darken-2 mt-0 mr-30">
                                Remind employees before their scheduled shift ends if no attendance clock‑out is found
                            </div>
                        </v-flex>
                        <v-flex xs12 sm2 class="form-row">
                            <v-select
                                v-model="beforeEndMinutes"
                                label="Minutes Before End"
                                :items="allowed_minutes"
                                :disabled="disabled || !beforeEnd"
                                class="minutes-select"
                            ></v-select>
                        </v-flex>
                    </v-layout>

                    <!-- After Shift End -->
                    <v-layout row wrap v-if="!loading">
                        <v-flex xs12 sm6 class="form-row">            
                            <v-switch
                                v-model="afterEnd"
                                label="After Shift End"
                                color="primary"
                                class="custom-switch"
                                hide-details
                            ></v-switch>
                            <div class="caption grey--text text--darken-2 mt-0 mr-30">
                                Remind employees after their shift ends if no attendance clock‑out is found
                            </div>
                        </v-flex>
                        <v-flex xs12 sm2 class="form-row">
                            <v-select
                                v-model="afterEndMinutes"
                                label="Minutes After End"
                                :items="allowed_minutes"
                                :disabled="disabled || !afterEnd"
                                class="minutes-select"
                            ></v-select>
                            </v-flex>
                    </v-layout>

                </v-card-text>

            </v-card>

        </v-flex>

    </v-layout> 
</template>

<script>

import attendanceRemindersApi from '@/api/attendancereminders'
import { getExpandCollapseVisibility, updateExpandCollapseVisibility } from '@/helpers/user-prefs'

export default {     
    name: 'AttendanceReminder',
    data () {
        const routeName = this.$route.name

        return {
            error: false,
            errorMessage: '',
            loading: false,
            disabled: false,
            attendanceReminderVisible: getExpandCollapseVisibility(routeName, 'attendaance-reminder-appearance'),
            allowed_minutes: [15, 30, 45, 60],            
            attendanceReminderSettingsId: null,
            beforeStart: true,
            beforeStartMinutes: 15,
            afterStart: true,
            afterStartMinutes: 15,
            beforeEnd: true,
            beforeEndMinutes: 15,
            afterEnd: true,
            afterEndMinutes: 15,
            initialized: false
        }
    },
    methods: {
        async getAttendanceReminderSettings(show_loading) {
            try {
                this.loading = show_loading
                const response = await attendanceRemindersApi.getSettings()
                if (response && response.status == 200) {
                    this.attendanceReminderSettingsId = response.data.attendanceReminderSettingsId
                    this.beforeStart = response.data.beforeStart
                    this.beforeStartMinutes = response.data.beforeStartMinutes || 15
                    this.afterStart = response.data.afterStart
                    this.afterStartMinutes = response.data.afterStartMinutes || 15
                    this.beforeEnd = response.data.beforeEnd
                    this.beforeEndMinutes = response.data.beforeEndMinutes || 15
                    this.afterEnd = response.data.afterEnd
                    this.afterEndMinutes = response.data.afterEndMinutes || 15
                }
                this.initialized = true
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async updateSettings() {
            try {
                const data = {
                    attendanceReminderSettingsId: this.attendanceReminderSettingsId,
                    beforeStart: this.beforeStart,
                    beforeStartMinutes: this.beforeStartMinutes,
                    afterStart: this.afterStart,
                    afterStartMinutes: this.afterStartMinutes,
                    beforeEnd: this.beforeEnd,
                    beforeEndMinutes: this.beforeEndMinutes,
                    afterEnd: this.afterEnd,
                    afterEndMinutes: this.afterEndMinutes
                }
                await attendanceRemindersApi.updateSettings(data)
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
    },
    mounted() {
        this.getAttendanceReminderSettings(true)
    },
    watch: {
        attendanceReminderVisible(newVal) {
            updateExpandCollapseVisibility(this.$route.name, 'attendaance-reminder-appearance', newVal)
        },
        beforeStart(){
            if (this.initialized) {
                this.updateSettings(false)
            }
        },
        beforeStartMinutes(){
            if (this.initialized) {
                this.updateSettings(false)
            }
        },
        afterStart(){
            if (this.initialized) {
                this.updateSettings(false)
            }
        },
        afterStartMinutes(){
            if (this.initialized) {
                this.updateSettings(false)
            }
        },
        beforeEnd(){
            if (this.initialized) {
                this.updateSettings(false)
            }
        },
        beforeEndMinutes(){
            if (this.initialized) {
                this.updateSettings(false)
            }
        },
        afterEnd(){
            if (this.initialized) {
                this.updateSettings(false)
            }
        },
        afterEndMinutes(){
            if (this.initialized) {
                this.updateSettings(false)
            }
        }
    },
}
</script>
<style scoped>
.form-row {
    padding: 0px 15px 0px 15px;
}
.minutes-select {
    max-width: 150px;
    margin-top: 20px;
}
.divider {
    border-top: 1px solid #C7C7C7;
    width: 95%;
    margin-top: 10px;
}
.divider-mobile {
    border-top: 1px solid #C7C7C7;
    width: 90%;
    margin-top: 10px;
}

/* Scoped override for Vuetify 2 */
.custom-switch ::v-deep .v-input__slot .v-label {
    font-size: 15px !important; /* !important ensures Vuetify defaults are overridden */
    font-weight: 500;
    letter-spacing: 0.5px;
    color: rgba(0, 0, 0, 0.87);
}
</style>